import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import { Shield, Lock, FileText, EyeOff, UserCheck, ChevronRight } from "lucide-react";
import GlassCard from "./GlassCard";

const PrivacySecuritySheet = ({ open, onOpenChange }) => {
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                className="max-h-[85vh] rounded-t-3xl flex flex-col pt-6"
                style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
            >
                <SheetHeader className="pb-4 flex-shrink-0 text-left">
                    <SheetTitle className="text-xl font-bold flex items-center gap-2">
                        <Shield className="w-5 h-5 text-primary" />
                        Privacy & Security
                    </SheetTitle>
                    <SheetDescription>
                        How we protect your data and personal information.
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto space-y-4 scrollbar-hide">
                    {/* Intro Card */}
                    <div className="bg-primary/5 p-4 rounded-2xl border border-primary/10">
                        <p className="text-sm font-medium text-foreground leading-relaxed">
                            Your trust is our top priority. HomeEzi is built to be <strong>private by default</strong>.
                        </p>
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed italic">
                            We do not require or verify your physical address. Use an "Alias" (like "Holiday Flat" or "My Home") to keep your real-world location disconnected from your appliance data.
                        </p>
                    </div>

                    {/* Section: POPIA */}
                    <GlassCard className="p-4 space-y-2">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-blue-500/10 rounded-lg">
                                <FileText className="w-5 h-5 text-blue-500" />
                            </div>
                            <h3 className="font-bold text-foreground">POPIA Compliance</h3>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            We respect your rights under the South African <strong>Protection of Personal Information Act (POPIA)</strong>.
                            We only collect the absolute minimum data required to power your Home Experience.
                            You have the right to request deletion of your data at any time.
                        </p>
                    </GlassCard>

                    {/* Section: Data Encryption */}
                    <GlassCard className="p-4 space-y-2">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-emerald-500/10 rounded-lg">
                                <Lock className="w-5 h-5 text-emerald-500" />
                            </div>
                            <h3 className="font-bold text-foreground">Secure Storage</h3>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            All your sensitive information is stored securely. We use industry-standard Row Level Security (RLS)
                            policies, ensuring that your data is mathematically inaccessible to other users.
                        </p>
                    </GlassCard>

                    {/* Section: Provider Connection */}
                    <GlassCard className="p-4 space-y-2">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-blue-500/10 rounded-lg">
                                <UserCheck className="w-5 h-5 text-blue-500" />
                            </div>
                            <h3 className="font-bold text-foreground">Service Provider Connection</h3>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            <span className="font-semibold text-blue-600">Purely optional.</span> If you enable "Notify Service Provider" in your settings,
                            we will automate an alert to your appliance's provider when a service is due.
                            This is the <strong>only</strong> time we share specific appliance data.
                        </p>
                    </GlassCard>

                    {/* Section: Zero Sharing */}
                    <GlassCard className="p-4 space-y-2">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-purple-500/10 rounded-lg">
                                <EyeOff className="w-5 h-5 text-purple-500" />
                            </div>
                            <h3 className="font-bold text-foreground">No Third-Party Sharing</h3>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            We are not a data broker. We do not sell your history to advertisers or insurers.
                            <strong>The only way a third party gets your data</strong> is if you manually "Hand Over" or share your vault with a real estate agent or new owner.
                        </p>
                    </GlassCard>

                    {/* Legal Links */}
                    <div className="pt-4 flex flex-col gap-2">
                        <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest px-2 mb-1">Detailed Legal Terms</p>
                        <div className="grid grid-cols-1 gap-2">
                            <button
                                onClick={() => window.location.href = '/privacy'}
                                className="flex items-center justify-between p-4 rounded-2xl bg-white border border-black/5 hover:bg-black/5 transition-colors"
                            >
                                <span className="text-sm font-medium">Privacy Policy</span>
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </button>
                            <button
                                onClick={() => window.location.href = '/terms'}
                                className="flex items-center justify-between p-4 rounded-2xl bg-white border border-black/5 hover:bg-black/5 transition-colors"
                            >
                                <span className="text-sm font-medium">Terms of Service</span>
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </button>
                            <button
                                onClick={() => window.location.href = '/refund'}
                                className="flex items-center justify-between p-4 rounded-2xl bg-white border border-black/5 hover:bg-black/5 transition-colors"
                            >
                                <span className="text-sm font-medium">Refund Policy</span>
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </button>
                        </div>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default PrivacySecuritySheet;
